// @file vuexstore for padlet authors/commentors information
import { JsonAPIResource, User } from '@padlet/arvo'
import { captureFetchException } from 'bits/error_tracker'
import sortBy from 'lodash.sortby'
import PadletApi from 'surface/padlet_api'
import type { Id } from 'types'
import Vue from 'vue'
import { Module } from 'vuex'
import { RootState } from 'vuexstore/surface/types'

interface UserContributorState {
  userById: Record<Id, User>
  userIdsBeingFetched: Id[]
  postAuthorIds: Id[]
}

const defaultState = (): UserContributorState => ({
  userById: {},
  userIdsBeingFetched: [],
  postAuthorIds: [],
})

const UserContributorModule: Module<UserContributorState, RootState> = {
  namespaced: true,
  state: defaultState,
  getters: {
    getUserById:
      (state): Function =>
      (id: Id): User | null =>
        state.userById[id],
    isUserIdBeingFetched:
      (state): Function =>
      (id: Id): boolean =>
        state.userIdsBeingFetched.includes(id),
    postAuthors: (state): User[] => Object.values(state.userById).filter((u) => state.postAuthorIds.includes(u.id)),
    registeredPostAuthors: (state, getters, rootState, rootGetters): User[] =>
      sortBy(
        getters.postAuthors.filter((u) => u.registered),
        (u: User): string | undefined => {
          return rootGetters.builder.id === u.id ? '' : u.short_name
        },
      ),
    anonymousPostAuthors: (_, getters): User[] => getters.postAuthors.filter((u) => !u.registered),
    totalPostAuthors: (state): number => state.postAuthorIds.length,
  },
  mutations: {
    ADD_USER(state, newUser): void {
      Vue.set(state.userById, newUser.id, newUser)
    },
    ADD_BEING_FETCHED_USER_ID(state, userId): void {
      state.userIdsBeingFetched.push(userId)
    },
    REMOVE_BEING_FETCHED_USER_ID(state, userId): void {
      state.userIdsBeingFetched = state.userIdsBeingFetched.filter((id) => id !== userId)
    },
    UPDATE_POST_AUTHOR_IDS(state, postAuthorIds): void {
      Vue.set(state, 'postAuthorIds', [...postAuthorIds])
    },
  },
  actions: {
    async fetchUser({ commit, getters }, userId: Id): Promise<void> {
      if (getters.getUserById(userId) || getters.isUserIdBeingFetched(userId)) {
        return
      }

      commit('ADD_BEING_FETCHED_USER_ID', userId)
      try {
        const fetchedUserData = await PadletApi.UserContributor.read(userId)
        commit('ADD_USER', (fetchedUserData.data as JsonAPIResource<User>).attributes)
        commit('REMOVE_BEING_FETCHED_USER_ID', userId)
      } catch (e) {
        captureFetchException(e, { source: 'fetchUserContributorInfo' })
      }
    },
    // For the contributors panel (Currently it only shows post authors)
    async fetchPostAuthors({ commit, dispatch, rootGetters }): Promise<void> {
      try {
        // The API WallContributorIds but it only return post author ids to match old behaviour for now
        const { contributorIds } = await PadletApi.UserContributor.readWallContributorIds({
          id: rootGetters.wallId,
        })
        commit('UPDATE_POST_AUTHOR_IDS', contributorIds)
        contributorIds.forEach((userId) => {
          dispatch('fetchUser', userId)
        })
      } catch (e) {
        captureFetchException(e, { source: 'fetchPostAuthors' })
      }
    },
  },
}

export default UserContributorModule
